import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef, ColumnFiltersState } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import { useGo, useNavigation, type HttpError } from "@refinedev/core";

import {
  ACTION_CARD_SEVERITIES,
  ACTION_CARD_STATUSES,
  type ActionCard,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/app/StatusBadge";
import { formatDateTime, humanize } from "@/lib/format";

import {
  DataTable,
  PageHeader,
  SearchBox,
  type RefineTableCore,
} from "../projects/_shared";
import { RowActions } from "../_shared/ListScaffold";
import { ActionCardLifecycleActions } from "./ActionCardLifecycleActions";
import { formatScore } from "./model";

export function ActionCardList() {
  const go = useGo();
  const { showUrl } = useNavigation();
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [severityFilter, setSeverityFilter] = React.useState("");
  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>([]);

  const columns = useMemo<ColumnDef<ActionCard, unknown>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Action",
        cell: ({ row }) => (
          <div className="flex max-w-[28rem] flex-col gap-1">
            <span className="font-medium [overflow-wrap:anywhere]">
              {row.original.title}
            </span>
            {row.original.summary ? (
              <span className="line-clamp-2 text-sm text-muted-foreground">
                {row.original.summary}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "severity",
        accessorKey: "severity",
        header: "Severity",
        cell: ({ row }) => (
          <Badge variant="secondary">{humanize(row.original.severity)}</Badge>
        ),
      },
      {
        id: "risk_level",
        accessorKey: "risk_level",
        header: "Risk",
        cell: ({ row }) => (
          <Badge variant="outline">{humanize(row.original.risk_level)}</Badge>
        ),
      },
      {
        id: "subject_type",
        accessorKey: "subject_type",
        header: "Subject",
        cell: ({ row }) => (
          <span className="text-sm">
            {humanize(row.original.subject_type)}
          </span>
        ),
      },
      {
        id: "approval_mode",
        accessorKey: "approval_mode",
        header: "Authority",
        cell: ({ row }) => (
          <span className="text-sm">{humanize(row.original.approval_mode)}</span>
        ),
      },
      {
        id: "priority_score",
        accessorKey: "priority_score",
        header: "Score",
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums">
            {formatScore(row.original.priority_score)}
          </span>
        ),
      },
      {
        id: "last_seen_at",
        accessorKey: "last_seen_at",
        header: "Last seen",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {formatDateTime(row.original.last_seen_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("action-cards", row.original.id) })}
            hideDelete
            extra={<ActionCardLifecycleActions card={row.original} compact />}
          />
        ),
      },
    ],
    [go, showUrl],
  );

  const table = useTable<ActionCard, HttpError>({
    columns,
    refineCoreProps: {
      resource: "action-cards",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "last_seen_at", order: "desc" }] },
      filters: { defaultBehavior: "replace" },
    },
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
  });

  const core = table.refineCore as unknown as RefineTableCore;
  const filterRunId = React.useRef(0);

  React.useEffect(() => {
    const runId = filterRunId.current + 1;
    filterRunId.current = runId;
    const handle = window.setTimeout(() => {
      if (runId !== filterRunId.current) return;
      const filters: ColumnFiltersState = [];
      const query = search.trim();
      if (query) {
        filters.push({
          id: "q",
          value: query,
          operator: "contains",
        } as ColumnFiltersState[number] & { operator: "contains" });
      }
      if (statusFilter) {
        filters.push({ id: "status", value: statusFilter });
      }
      if (severityFilter) {
        filters.push({ id: "severity", value: severityFilter });
      }
      setColumnFilters(filters);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search, statusFilter, severityFilter]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Action Cards"
        description="Ranked proposals for what should happen next inside governed contexts."
        icon="actionCards"
        action={
          <Button variant="outline" onClick={() => go({ to: "/contexts" })}>
            <Icon name="contexts" size={16} className="mr-2" aria-hidden />
            Context Atlas
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search action cards..."
        />
        <Select
          value={statusFilter || "__all__"}
          onValueChange={(value) =>
            setStatusFilter(value === "__all__" ? "" : value)
          }
        >
          <SelectTrigger className="w-[210px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {ACTION_CARD_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {humanize(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={severityFilter || "__all__"}
          onValueChange={(value) =>
            setSeverityFilter(value === "__all__" ? "" : value)
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All severities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All severities</SelectItem>
            {ACTION_CARD_SEVERITIES.map((severity) => (
              <SelectItem key={severity} value={severity}>
                {humanize(severity)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No action cards match the current filters."
        onRowClick={(card) => go({ to: showUrl("action-cards", card.id) })}
      />
    </div>
  );
}

export default ActionCardList;
